import { useCallback, useMemo, useState } from "react";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";

import type * as Model from "../../../../model";
import type { QueryVariables } from "../../../../model/use-case/use-search-reservation-list-query-data";

type ReservationType = Model.Constants.ReservationTypeForSearchReservationList;
/**
 * 日付での検索
 */
type DateRangeCase = {
	reservationType: Exclude<ReservationType, "reservation_id">;
	from?: string;
	to?: string;
};
/**
 * フォームでの検索
 */
type InputCase = {
	reservationType: Extract<ReservationType, "reservation_id">;
	reservationID: string;
};

type SearchState = DateRangeCase | InputCase;

export const useHandleSearchActions = (
	// 検索条件
	queryVariables: QueryVariables,
	// 予約の検索
	onSearchReservationList: (props: Model.UseCase.SearchReservationListProps) => void,
) => {
	const [searchState, setSearchState] = useState<SearchState>(
		queryVariables.type === "reservation_id"
			? {
					reservationType: queryVariables.type,
					reservationID: queryVariables.reservationId,
				}
			: {
					reservationType: queryVariables.type,
					from: queryVariables.from,
					to: queryVariables.to,
				},
	);
	/**
	 * 予約検索タイプの変更
	 */
	const handleChangeReservationType = useCallback((reservationType: ReservationType) => {
		if (reservationType === "reservation_id") {
			setSearchState((prev) => ({ ...prev, reservationType, reservationID: "" }));
		} else {
			setSearchState((prev) => ({ ...prev, reservationType }));
		}
	}, []);

	/**
	 * 日付の変更
	 */
	const handleChangeFromAndTo = useCallback(({ from, to }: DateRange) => {
		setSearchState((prev) => ({ ...prev, from: from, to: to }));
	}, []);

	/**
	 * 予約 ID の変更
	 */
	const handleChangeReservationID = useCallback((reservationID: string) => {
		setSearchState((prev) => ({ ...prev, reservationID }));
	}, []);

	/**
	 * 日付での検索
	 */
	const onSearchReservationListByRangeDate = useCallback(
		(reservationType: Exclude<ReservationType, "reservation_id">, from: string, to: string) => {
			onSearchReservationList({ type: reservationType, from, to });
		},
		[onSearchReservationList],
	);
	/**
	 * 予約番号での検索
	 */
	const onSearchReservationListByReservationID = useCallback(
		(reservationType: Extract<ReservationType, "reservation_id">, reservationId: string) => {
			onSearchReservationList({ type: reservationType, reservationId });
		},
		[onSearchReservationList],
	);
	/**
	 * 予約の検索
	 */
	const handleClickSearchReservationList = useCallback(() => {
		if (searchState.reservationType === "reservation_id") {
			onSearchReservationListByReservationID(searchState.reservationType, searchState.reservationID);
		} else {
			if (!searchState.from) return;

			const from = searchState.from;
			/**
			 * 日付の範囲が指定されていない場合は、開始日と終了日を同じにする
			 */
			const to = searchState.to ?? from;
			onSearchReservationListByRangeDate(searchState.reservationType, from, to);
		}
	}, [onSearchReservationListByRangeDate, onSearchReservationListByReservationID, searchState]);

	const disabled = useMemo(() => {
		if (searchState.reservationType === "reservation_id") {
			return !searchState.reservationID;
		}

		return !(searchState.from && searchState.to);
	}, [searchState]);

	return {
		//
		searchState,
		disabled,
		//
		// 予約タイプの変更
		handleChangeReservationType,
		// 日付の変更
		handleChangeFromAndTo,
		// 予約 ID の変更
		handleChangeReservationID,
		// 予約の検索
		handleClickSearchReservationList,
	};
};
